import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Trash2, User, ShieldAlert } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/54jbluz3_IMG-20260904-WA0003.jpg";

const EFFECTIVE_DATE = "December 9, 2026";

const Section = ({ n, title, children }) => (
  <section className="mb-14" data-testid={`delete-section-${n}`}>
    <div className="flex items-baseline gap-4 mb-4">
      <span className="h-display text-2xl md:text-3xl text-[#4FA8E0]/70">
        {n.toString().padStart(2, "0")}
      </span>
      <h2 className="h-display text-2xl md:text-3xl text-white tracking-[-0.02em]">
        {title}
      </h2>
    </div>
    <div className="text-white/65 leading-relaxed text-[15px] space-y-3 pl-0 md:pl-14">
      {children}
    </div>
  </section>
);

const Step = ({ n, title, body }) => (
  <div className="flex gap-4 md:gap-5 py-4 border-b border-white/8 last:border-b-0">
    <div className="shrink-0 w-10 h-10 rounded-full bg-[#4FA8E0]/15 border border-[#4FA8E0]/40 flex items-center justify-center text-[#9bd1f5] h-display font-bold">
      {n}
    </div>
    <div>
      <div className="h-display text-white text-base md:text-lg tracking-[-0.01em]">
        {title}
      </div>
      <div className="text-white/55 text-sm mt-1 leading-relaxed">{body}</div>
    </div>
  </div>
);

const DeleteAccount = () => {
  useEffect(() => {
    document.title = "Delete Account · Andromis Arcade";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App grain relative min-h-screen" data-testid="delete-account-page">
      <header className="relative border-b border-white/5 py-8 px-6 md:px-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
            data-testid="delete-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <img src={LOGO_URL} alt="Andromis Games" className="h-9 w-auto object-contain max-w-[120px]" />
            <span className="hidden md:inline text-xs tracking-[0.3em] uppercase text-white/50">
              Back to home
            </span>
          </Link>
          <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">Legal</span>
        </div>
      </header>

      <main className="relative py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="mb-4">
            <div className="eyebrow mb-4 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
              Delete Account
            </div>
            <h1
              className="h-display text-4xl md:text-6xl text-white tracking-[-0.03em] leading-[1]"
              data-testid="delete-title"
            >
              <span className="chrome-text">Delete Your</span>
              <br />
              <span className="italic font-light text-white/70">
                Andromis Arcade account.
              </span>
            </h1>
            <p className="mt-6 text-white/55 text-sm">
              Applies to:{" "}
              <span className="text-white/80">Andromis Arcade: Mini Games</span>{" "}
              on Android · Effective date:{" "}
              <span className="text-white/80">{EFFECTIVE_DATE}</span>
            </p>
          </div>

          <div className="hairline my-10 opacity-40" />

          {/* Intro */}
          <div className="text-white/70 leading-relaxed mb-14 text-base">
            <p className="mb-4">
              Andromis Arcade: Mini Games uses a real user account so you can
              save progress, XP per game, and appear on the leaderboard. If
              you no longer want to use the app, you can permanently delete
              your account and your server-side data at any time.
            </p>
            <p>
              This page explains{" "}
              <span className="text-white">
                exactly how to delete your account from inside the app
              </span>
              , what data is removed, and what is retained (if any) and for
              how long.
            </p>
          </div>

          {/* Highlight box */}
          <div
            className="mb-14 rounded-2xl border border-[#4FA8E0]/30 bg-[#4FA8E0]/[0.05] p-6 md:p-8"
            data-testid="delete-highlight"
          >
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-11 h-11 rounded-full bg-[#4FA8E0]/15 border border-[#4FA8E0]/40 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-[#9bd1f5]" />
              </div>
              <div>
                <div className="h-display text-white text-lg md:text-xl mb-1">
                  You can delete your account directly inside the app.
                </div>
                <p className="text-white/60 text-sm leading-relaxed">
                  Andromis Arcade includes a{" "}
                  <span className="text-white">Delete Account</span> button in
                  the app’s <span className="text-white">Profile</span>{" "}
                  screen. Confirming it permanently removes your account and
                  associated data from our systems.
                </p>
              </div>
            </div>
          </div>

          <Section n={1} title="Delete from inside the app">
            <p className="mb-3">
              Follow these steps to delete your Andromis Arcade account:
            </p>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-5 md:px-6" data-testid="delete-steps">
              <Step
                n={1}
                title="Open Andromis Arcade"
                body="Launch the Andromis Arcade: Mini Games app on your Android device."
              />
              <Step
                n={2}
                title="Sign in to your account"
                body="Enter your email and password to sign in. You must be signed in to delete your account."
              />
              <Step
                n={3}
                title="Go to Profile"
                body="Tap the Profile tab in the bottom navigation bar."
              />
              <Step
                n={4}
                title="Scroll to Delete Account"
                body="At the bottom of the Profile screen you will find the Delete Account section."
              />
              <Step
                n={5}
                title="Tap Delete Account and confirm"
                body="Read the on-screen warning and confirm the deletion. This action is permanent and cannot be undone."
              />
            </div>
          </Section>

          <Section n={2} title="What is deleted">
            <p>
              When you confirm{" "}
              <span className="text-white">Delete Account</span> inside the
              app, we permanently delete your account and all associated data
              from our systems. This is a real account deletion, not a logout
              and not a profile hide.
            </p>
            <p className="mt-3">The following is deleted immediately:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="text-white">Account credentials</span>: email
                address, password hash, and authentication tokens.
              </li>
              <li>
                <span className="text-white">Profile information</span>:
                display name, country (if provided), and favorite game
                preference.
              </li>
              <li>
                <span className="text-white">Gameplay data</span>: per-game XP,
                level progress, best scores, best times, stars, and accuracy
                records.
              </li>
              <li>
                <span className="text-white">Leaderboard entries</span>: your
                results are removed from public rankings.
              </li>
              <li>
                <span className="text-white">Session data</span>: active login
                tokens are invalidated on all devices.
              </li>
              <li>
                <span className="text-white">Local device cache</span>:
                signed-out and cleared from the device that initiated the
                deletion.
              </li>
            </ul>
            <div className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] p-4 text-emerald-200/90 text-sm flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Delete Account fully removes your account and its server-side
                data. It does not merely log you out or hide your profile.
              </span>
            </div>
          </Section>

          <Section n={3} title="What is retained, why, and for how long">
            <p>
              A limited amount of technical data may be retained for a short
              period after account deletion, only for security, fraud
              prevention, and legal compliance purposes. Nothing on this list
              is used to reconstruct your profile or personalize the app.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="text-white">Server backups</span>: routine
                encrypted backups may contain a copy of your account until
                they roll off. Backups are automatically overwritten within{" "}
                <span className="text-white">up to 30 days</span>, after which
                no copy remains.
              </li>
              <li>
                <span className="text-white">
                  Security &amp; abuse logs
                </span>
                : IP addresses, request logs, and rate-limit records tied to
                your account may be retained for up to{" "}
                <span className="text-white">90 days</span> to detect fraud,
                abuse, and security incidents. These logs are not linked back
                to a user profile after deletion.
              </li>
              <li>
                <span className="text-white">
                  Legal &amp; regulatory records
                </span>
                : where required by law (for example, tax, accounting, or
                dispute resolution), we may retain minimal records for the
                period required by that law.
              </li>
              <li>
                <span className="text-white">
                  Third-party advertising data (Google AdMob)
                </span>
                : AdMob and Google Play Services may retain limited
                advertising, diagnostic, and fraud-prevention data under
                Google’s own policies. This data is not controlled by Andromis
                Games. You can reset or delete your Android Advertising ID at
                any time through{" "}
                <span className="text-white">
                  Settings → Google → Ads
                </span>{" "}
                on your device.
              </li>
            </ul>
            <p>
              Once these retention windows elapse, no residual account data
              remains in our production systems or backups.
            </p>
          </Section>

          <Section n={4} title="If you cannot access your account">
            <p>
              If you cannot sign in (for example, forgotten password or lost
              access to the device) and cannot use the in-app{" "}
              <span className="text-white">Delete Account</span> option, you
              can request account deletion by email. We will verify your
              identity and process the deletion for you.
            </p>
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="h-display text-white text-lg mb-1 flex items-center gap-2">
                <User className="w-4 h-4 text-[#4FA8E0]" />
                Support · Andromis Games LLC
              </div>
              <a
                href="mailto:miky@andromisgames.com?subject=Andromis%20Arcade%20-%20Delete%20Account%20Request"
                className="inline-flex items-center gap-2 text-[#9bd1f5] hover:text-white transition-colors mt-2"
                data-testid="delete-contact-email"
              >
                <Mail className="w-4 h-4" />
                miky@andromisgames.com
              </a>
              <p className="text-white/50 text-sm mt-3 leading-relaxed">
                Please email us from the address associated with your account
                and use the subject line{" "}
                <span className="text-white">
                  “Andromis Arcade – Delete Account Request”
                </span>
                . We will respond within a reasonable time.
              </p>
            </div>
          </Section>

          <Section n={5} title="Related policies">
            <p>
              For details on what data Andromis Arcade collects and how it is
              processed, please review our{" "}
              <Link
                to="/privacy-policy"
                className="text-[#9bd1f5] underline hover:text-white"
                data-testid="delete-privacy-link"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </Section>
        </div>
      </main>

      <footer className="border-t border-white/5 py-8 px-6 md:px-10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Andromis" className="h-8 w-auto object-contain max-w-[110px]" />
            <span className="text-white/40 text-xs tracking-[0.3em] uppercase">LLC</span>
          </div>
          <div className="text-white/30 text-xs tracking-[0.2em] uppercase">
            © {new Date().getFullYear()} Andromis Games LLC · All rights reserved
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DeleteAccount;
